import {IProductSnippet} from 'shared/types/productSnippet.types';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    useAddToCartMutation,
    useDeleteFromCartMutation,
    useGetCartQuery,
    useUpdateCartMutation,
} from 'modules/cart/services';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {IProductShort} from 'modules/products/types';
import _debounce from 'lodash/debounce';
import {actionSetAddedProduct} from 'modules/cart/slice';
import {ICartProduct} from 'modules/cart/models';
import {useReservationFlow} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useReservationFlow';

type TUseCartParams = {
    productData: IProductShort;
    isPrive: boolean;
    isWorker: boolean;
    isLoggedIn: boolean;
    availability: 'IN_STOCK' | 'OUT_OF_STOCK' | 'FOR_RESERVATION';
    onAddToCartClick: (quantityDelta: number, cartQuantityAfter: number) => void;
    onRemoveFromCartClick: (quantityDelta: number, cartQuantityAfter: number) => void;
};

export const useCart = (params: TUseCartParams): IProductSnippet['cart'] => {
    const dispatch = useAppDispatch();

    const {productData, onAddToCartClick, onRemoveFromCartClick, isWorker, isPrive, isLoggedIn, availability} = params;

    const {
        storesWithStockCount,
        isReservationFlowActive,
        reserveButtonText,
        onReserve: onReserveInner,
    } = useReservationFlow({
        productData,
        isWorker,
        isPrive,
        isLoggedIn,
        availability,
        onAddToCartClick,
    });

    const {offerId} = productData;
    const [deleteProduct, {isLoading: isDeleteLoading}] = useDeleteFromCartMutation();
    const [addToCart, {isLoading: isAddingToCart}] = useAddToCartMutation();
    const [updateCart, {isLoading: isUpdatingCart}] = useUpdateCartMutation();
    const [deleteFromCart, {isLoading: isDeletingFromCart}] = useDeleteFromCartMutation();
    const {data: cartData} = useGetCartQuery({
        mini: true,
    });

    const productInCartData = useMemo(
        () => (cartData?.products ?? []).find((product) => product.offerId === offerId)?.cartData,
        [cartData?.products, offerId],
    );

    const quantity = useMemo(() => productInCartData?.quantity || 0, [productInCartData?.quantity]);

    const cartSnapshotRef = useRef({
        cartLineId: productInCartData?.id,
        offerId,
        quantity,
    });
    cartSnapshotRef.current = {
        cartLineId: productInCartData?.id,
        offerId,
        quantity,
    };

    const [localQuantity, setLocalQuantity] = useState<number>(quantity);

    useEffect(() => {
        setLocalQuantity(quantity);
    }, [quantity]);

    const isPendingQuantityChange = isUpdatingCart || isDeletingFromCart || isAddingToCart || isDeleteLoading;

    const onAddOne = useCallback(async () => {
        if (isPendingQuantityChange || quantity === productData.stockAmount) {
            return;
        }

        const prevQty = quantity;

        const response = await addToCart({
            offerId: productData.offerId,
            quantity: quantity + 1,
        }).unwrap();

        const newProduct = response.products.find((item) => item.offerId === productData.offerId);
        const newQty = newProduct?.cartData?.quantity ?? prevQty + 1;

        onAddToCartClick(Math.max(0, newQty - prevQty), newQty);

        dispatch(actionSetAddedProduct(newProduct as ICartProduct));
    }, [isPendingQuantityChange, quantity, productData, addToCart, onAddToCartClick, dispatch]);

    const onRemoveOne = useCallback(async () => {
        if (isPendingQuantityChange || 0 === quantity) {
            return;
        }

        const prevQty = quantity;

        if (1 < quantity) {
            const response = await updateCart({id: productInCartData?.id, quantity: quantity - 1}).unwrap();
            const newProduct = response.products.find((item) => item.offerId === offerId);
            const newQty = newProduct?.cartData?.quantity ?? prevQty - 1;

            onRemoveFromCartClick(prevQty - newQty, newQty);
        } else {
            await deleteProduct({id: productInCartData?.id}).unwrap();

            onRemoveFromCartClick(1, 0);
        }
    }, [
        deleteProduct,
        isPendingQuantityChange,
        offerId,
        onRemoveFromCartClick,
        productInCartData?.id,
        quantity,
        updateCart,
    ]);

    const debouncedUpdate = useMemo(
        () =>
            _debounce(async (value: number) => {
                const {cartLineId, offerId: oid, quantity: prevQty} = cartSnapshotRef.current;
                if (0 === value) {
                    if (undefined === cartLineId) {
                        return;
                    }
                    await deleteFromCart({id: cartLineId}).unwrap();
                    onRemoveFromCartClick(prevQty, 0);
                    return;
                }

                if (undefined === cartLineId) {
                    return;
                }

                const response = await updateCart({id: cartLineId, quantity: value}).unwrap();
                const updated = response.products.find((product) => product.offerId === oid)?.cartData;
                const newQty = updated?.quantity ?? value;

                if (newQty > prevQty) {
                    onAddToCartClick(newQty - prevQty, newQty);
                } else if (newQty < prevQty) {
                    onRemoveFromCartClick(prevQty - newQty, newQty);
                }
            }, 500),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [deleteFromCart, updateCart],
    );

    useEffect(() => {
        return () => {
            debouncedUpdate.cancel();
        };
    }, [debouncedUpdate]);

    const onQuantityChange = useCallback(
        (value: number) => {
            if (0 === value) {
                debouncedUpdate(0);
                setLocalQuantity(0);
                return;
            }

            if (!/^\d+$/.test(value.toString())) {
                return;
            }
            const newQuantity = Math.max(0, Math.min(productData.stockAmount, value));
            debouncedUpdate(newQuantity);
            setLocalQuantity(newQuantity);
        },
        [debouncedUpdate, productData.stockAmount],
    );

    const onGoToCart = useCallback(() => {}, []);
    const onSubscribe = useCallback(() => {}, []);
    const onReserve = useCallback(() => {
        onReserveInner();
    }, [onReserveInner]);

    return {
        storesWithStockCount,
        isReservationFlowActive,
        reserveButtonText,
        onAddOne,
        onRemoveOne,
        onQuantityChange,
        onGoToCart,
        onReserve,
        onSubscribe,
        isPendingQuantityChange,
        quantity: localQuantity,
    };
};
